import { useEffect, useRef, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Calendar, Users, X } from 'lucide-react';
import {
  ownerLabel,
  type ExecutionOwner,
  type ExecutionObjective,
  type ExecutionKind,
  type ExecutionNode,
} from './model';

export const kindLabels: Record<ExecutionKind, string> = {
  objective: 'Objective',
  strategy: 'Strategy',
  project: 'Project',
  task: 'Task',
};
export const childKind = (type: ExecutionKind): 'strategy' | 'project' | 'task' =>
  type === 'objective' ? 'strategy' : type === 'strategy' ? 'project' : 'task';
export const pluralKind = (type: 'strategy' | 'project' | 'task') =>
  type === 'strategy' ? 'strategies' : `${type}s`;
export function useExecutionActive() {
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  return active;
}
export function formatExecutionDate(value: string | null | undefined, withYear = false) {
  if (!value) return 'No target date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  }).format(date);
}
export function describeError(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'We couldn’t save this change. Your edits are retained.';
}
export function Owner({ owner }: { owner: ExecutionOwner }) {
  return (
    <div
      className={`execution-owner ${!owner.userId && (!owner.positionId || owner.vacant) ? 'missing' : ''}`}
    >
      <Users size={13} aria-hidden="true" />
      <span>{ownerLabel(owner)}</span>
    </div>
  );
}
export function DueDate({ value }: { value: string | null }) {
  return (
    <span className="execution-date">
      <Calendar size={12} aria-hidden="true" />
      {formatExecutionDate(value, true)}
    </span>
  );
}
export function DeliveryProgress({ value }: { value: number }) {
  return (
    <div>
      <div className="execution-progress-label">
        <span>Delivery completion</span>
        <strong>{Math.round(value)}%</strong>
      </div>
      <div
        className="execution-progress-track"
        role="progressbar"
        aria-label="Delivery completion"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
      <p className="execution-progress-note">Completion of supporting work</p>
    </div>
  );
}
const paceLabels: Record<ExecutionObjective['outcome']['pace'], string> = {
  ahead: 'Ahead of pace',
  on_pace: 'On pace',
  behind: 'Behind pace',
  unavailable: 'Pace unavailable',
  pending: 'Pending',
  overdue: 'Target overdue',
  achieved: 'Target achieved',
};
export function OutcomeProgress({
  objective,
  detailed = false,
}: {
  objective: ExecutionObjective;
  detailed?: boolean;
}) {
  const { primaryMeasure: measure, outcome } = objective;
  const valueLabel = !measure
    ? 'Not set up'
    : measure.kind === 'milestone'
      ? outcome.milestoneAchieved === null
        ? 'No update yet'
        : outcome.milestoneAchieved
          ? 'Achieved'
          : 'Not achieved'
      : outcome.currentValue === null
        ? 'No reading yet'
        : `${outcome.currentValue.toLocaleString()}${measure.unit ? ` ${measure.unit}` : ''}`;
  return (
    <div>
      <div className="execution-progress-label">
        <span>Objective outcome</span>
        <strong>{valueLabel}</strong>
      </div>
      <div className="execution-progress-track outcome">
        <span style={{ width: `${Math.max(0, Math.min(100, outcome.progressPercent ?? 0))}%` }} />
      </div>
      <p className="execution-progress-note">
        {measure?.kind === 'numeric'
          ? `${measure.baseline.toLocaleString()} → ${measure.target.toLocaleString()} ${measure.unit}${outcome.progressPercent !== null ? ` · ${Math.round(outcome.progressPercent)}% towards target` : ''} · `
          : ''}
        {measure ? paceLabels[outcome.pace] : 'Define a primary measure to track movement'}
      </p>
      {detailed && measure?.kind === 'numeric' && outcome.expectedValue !== null && (
        <p className="execution-progress-note">
          Expected as of {formatExecutionDate(outcome.latestReadingAt, true)}:{' '}
          {Number(outcome.expectedValue.toFixed(2)).toLocaleString()} {measure.unit}
          {outcome.paceGap !== null
            ? ` · Difference: ${Number(outcome.paceGap.toFixed(2)).toLocaleString()} ${measure.unit}`
            : ''}
          . Pace uses a straight line from baseline date to target date.
        </p>
      )}
      <p className="execution-progress-note">
        Latest outcome reading:{' '}
        {outcome.latestReadingAt
          ? formatExecutionDate(outcome.latestReadingAt, true)
          : 'Not recorded'}
        {outcome.stale ? ' · Evidence needs updating' : ''}
      </p>
      {detailed && (
        <p className="execution-progress-note">
          Latest owner assessment:{' '}
          {outcome.latestHealthAt
            ? formatExecutionDate(outcome.latestHealthAt, true)
            : 'Not recorded'}
          . An assessment does not replace an outcome reading.
        </p>
      )}
    </div>
  );
}
export function Health({ objective }: { objective: ExecutionObjective }) {
  const health = objective.outcome.ownerHealth;
  return (
    <span className={`execution-health ${health ?? (objective.outcome.stale ? 'stale' : '')}`}>
      {health
        ? `${health.replace(/_/g, ' ')} · owner reported`
        : objective.outcome.stale
          ? 'Update overdue'
          : 'No owner assessment'}
    </span>
  );
}
export function WorkStatus({ node }: { node: ExecutionNode }) {
  return (
    <span className={`execution-health ${node.status}`}>{node.status.replace(/_/g, ' ')}</span>
  );
}
export function Modal({
  title,
  description,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  description: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const opener = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body &&
      document.activeElement !== document.documentElement
      ? document.activeElement
      : null
  );
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="execution-overlay" />
        <Dialog.Content
          className={`execution-dialog ${wide ? 'wide' : ''}`}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (opener.current?.isConnected) {
              opener.current.focus();
              if (document.activeElement === opener.current) return;
            }
            const main =
              document.getElementById('company-workspace-main') ?? document.querySelector('main');
            if (main instanceof HTMLElement) {
              if (!main.hasAttribute('tabindex')) main.tabIndex = -1;
              main.focus();
            }
          }}
        >
          <Dialog.Title asChild>
            <h2>{title}</h2>
          </Dialog.Title>
          <Dialog.Description className="execution-dialog-description">
            {description}
          </Dialog.Description>
          <Dialog.Close className="execution-dialog-close" aria-label={`Close ${title}`}>
            <X size={19} />
          </Dialog.Close>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
